import { Song } from '~/interfaces';
import { measureTrack } from '~/modules/songs/utils/measure-track';
import { calculateSongDifficulty, DifficultyLevel } from '~/modules/songs/utils/song-difficulty';
import { difficultyLevels } from '~/routes/sing-a-song/song-selection/components/song-settings/difficulty-levels';

const LEVEL_CLASS: Record<DifficultyLevel, string> = {
  Easy: 'text-green-400',
  Medium: 'text-yellow-300',
  Hard: 'text-orange-400',
  Expert: 'text-red-400',
};

interface Props {
  song: Song;
  tolerance: number;
}

/**
 * How demanding the singing just done was - measured from its notes, not from the tolerance the
 * players chose, which is a separate thing also called "difficulty" in the song settings.
 *
 * Measured from the merged track rather than read from the note index, which holds `tracks[0]`
 * alone. Every player count but two sings the merged track (`isMergedTrack`), and at two the pair
 * cover both parts between them - so on a duet the index describes half of what was actually sung.
 * Of the 124 multi-track songs shipped, 16 land on a different level once the whole of what was
 * sung is measured. For a song with one track the two are the same reading.
 *
 * The rating is left out for a song with no notes to measure, rather than guessing a level.
 *
 * The level the players chose sits beside it. Both used to be called "difficulty", and a player who
 * chose Easy read the rating's Medium as the game ignoring their choice.
 */
export default function SongDifficultyChip({ song, tolerance }: Props) {
  const measured = measureTrack(song, song.mergedTrack);
  const playedOn = difficultyLevels(true)[tolerance - 1];

  if (!measured && !playedOn) return null;

  const rating = measured && calculateSongDifficulty(measured);

  return (
    <span className="typography flex gap-2 self-start">
      {playedOn && (
        <span className="rounded-full bg-black/50 px-3 py-0.5 text-sm" data-test="played-on" data-level={playedOn}>
          Played on: <span className="font-bold">{playedOn}</span>
        </span>
      )}
      {rating && (
        <span
          className="rounded-full bg-black/50 px-3 py-0.5 text-sm"
          data-test="song-difficulty"
          data-level={rating.level}
          title={`${rating.factors.density.toFixed(1)} notes per second, ${rating.factors.span} semitones`}>
          Song rating: <span className={`font-bold ${LEVEL_CLASS[rating.level]}`}>{rating.level}</span>
        </span>
      )}
    </span>
  );
}
